/**
 * useSystemOrganization Hook
 * Standardized TanStack Query hooks for system organization management
 *
 * Migration: Replaces Zustand systemStore with TanStack Query
 * Pattern: Uses createQueryHookNoParams and createMutationHook factories
 * Previous: Zustand store with localStorage persistence in stores/systemStore.ts
 * Current: TanStack Query with memory cache (no localStorage persistence)
 *
 * Benefits:
 * - Proper server state management (not client state)
 * - Automatic cache invalidation and refetching
 * - Consistent with other API hooks in the codebase
 * - No localStorage pollution with server data
 */

import { useEffect } from 'react';
import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from '@/hooks/api/factories';
import type { Organization } from '@/types/common';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * System settings subset (for settings-only updates)
 */
export interface SystemSettings {
  branding?: {
    primary_color?: string;
    secondary_color?: string;
    logo_url?: string | null;
    company_name?: string | null;
  };
  features?: {
    chat_enabled?: boolean;
    faq_enabled?: boolean;
    document_upload?: boolean;
    user_registration?: boolean;
    email_verification?: boolean;
  };
  limits?: {
    max_users?: number | null;
    max_documents?: number | null;
    max_storage_gb?: number | null;
    max_file_size_mb?: number;
  };
  security?: {
    password_min_length?: number;
    password_require_uppercase?: boolean;
    password_require_lowercase?: boolean;
    password_require_digit?: boolean;
    password_require_special?: boolean;
    session_timeout_minutes?: number;
    max_login_attempts?: number;
  };
}

/**
 * Branding settings subset (for branding-only updates)
 */
export interface BrandingSettings {
  primary_color?: string;
  secondary_color?: string;
  logo_url?: string | null;
  company_name?: string | null;
}

/**
 * Organization update payload (partial updates)
 */
export interface OrganizationUpdate {
  name?: string | null;
  description?: string | null;
  settings?: SystemSettings | null;
  metadata?: {
    contact_email?: string | null;
    contact_phone?: string | null;
    timezone?: string;
    language?: string;
    country?: string;
  } | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const systemOrganizationQueryKeys = {
  all: ['system'] as const,
  organization: () => [...systemOrganizationQueryKeys.all, 'organization'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get system organization data.
 * Uses VERY_LONG stale time (30 minutes) as system organization rarely changes.
 *
 * The system organization contains:
 * - Basic info: id, name, slug, description
 * - Settings: branding, features, limits, security
 * - Metadata: contact info, timezone, language
 *
 * Usage:
 * @example
 * const { data: organization, isLoading } = useGetSystemOrganization();
 * console.log(organization?.name); // "SmartDoc"
 * console.log(organization?.settings?.branding?.primary_color); // "#1976d2"
 */
export const useGetSystemOrganization = createQueryHookNoParams<Organization>({
  queryKey: () => systemOrganizationQueryKeys.organization(),
  queryFn: (apiClient) =>
    apiClient.getSystemOrganizationApiSystemGet().then(response => {
      log.api.response('GET', '/api/system', 200, {
        name: response.data.name,
        slug: response.data.slug
      });
      return response.data as Organization;
    }),
  logContext: 'GET /api/system',
  staleTime: QUERY_STALE_TIME.VERY_LONG, // 30 minutes - system org rarely changes
});

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to update system organization (full update).
 * Invalidates system organization query on success.
 *
 * Usage:
 * @example
 * const updateOrg = useUpdateSystemOrganization();
 * await updateOrg.mutateAsync({
 *   name: 'New Company Name',
 *   description: 'Updated description',
 *   settings: { ... }
 * });
 */
export const useUpdateSystemOrganization = createMutationHook<Organization, OrganizationUpdate>({
  mutationFn: (apiClient, updates) =>
    apiClient.updateSystemOrganizationApiSystemPut(updates).then(response => {
      log.api.response('PUT', '/api/system', 200, {
        name: response.data.name,
        updated_fields: Object.keys(updates)
      });
      return response.data as Organization;
    }),
  invalidateQueries: [
    systemOrganizationQueryKeys.organization(),
  ],
  logContext: 'PUT /api/system',
  logMethod: 'PUT',
  onSuccess: (_data, updates) => {
    log.user.action('system_organization_updated', 'SystemOrganization', {
      updated_fields: Object.keys(updates)
    });
  },
});

/**
 * Hook to update system settings only.
 * Invalidates system organization query on success.
 *
 * Usage:
 * @example
 * const updateSettings = useUpdateSystemSettings();
 * await updateSettings.mutateAsync({
 *   branding: {
 *     primary_color: '#1976d2',
 *     company_name: 'SmartDoc AI'
 *   },
 *   features: {
 *     chat_enabled: true
 *   }
 * });
 */
export const useUpdateSystemSettings = createMutationHook<SystemSettings, SystemSettings>({
  mutationFn: (apiClient, settings) =>
    apiClient.updateOrganizationSettingsApiSystemSettingsPut(settings).then(response => {
      log.api.response('PUT', '/api/system/settings', 200, {
        updated_sections: Object.keys(settings)
      });
      return response.data as SystemSettings;
    }),
  invalidateQueries: [
    systemOrganizationQueryKeys.organization(),
  ],
  logContext: 'PUT /api/system/settings',
  logMethod: 'PUT',
  onSuccess: (_data, settings) => {
    log.user.action('system_settings_updated', 'SystemOrganization', {
      updated_sections: Object.keys(settings)
    });
  },
});

/**
 * Hook to update branding settings only.
 * Invalidates system organization query on success.
 *
 * Usage:
 * @example
 * const updateBranding = useUpdateBranding();
 * await updateBranding.mutateAsync({
 *   primary_color: '#1976d2',
 *   secondary_color: '#dc004e',
 *   company_name: 'SmartDoc AI'
 * });
 */
export const useUpdateBranding = createMutationHook<BrandingSettings, BrandingSettings>({
  mutationFn: (apiClient, branding) =>
    apiClient.updateBrandingSettingsApiSystemSettingsBrandingPut(branding).then(response => {
      log.api.response('PUT', '/api/system/settings/branding', 200, {
        updated_fields: Object.keys(branding)
      });
      return response.data as BrandingSettings;
    }),
  invalidateQueries: [
    systemOrganizationQueryKeys.organization(),
  ],
  logContext: 'PUT /api/system/settings/branding',
  logMethod: 'PUT',
  onSuccess: (_data, branding) => {
    log.user.action('branding_updated', 'SystemOrganization', {
      updated_fields: Object.keys(branding)
    });
  },
});

// ============================================================================
// Convenience Hook (Backward Compatibility)
// ============================================================================

/**
 * Convenience hook that provides system organization with a simple API.
 * Maintains backward compatibility with the old useSystem() hook from systemStore.
 *
 * Returns:
 * - system: Organization data or null
 * - isLoading: Loading state
 * - updateSystem: Function to update full organization
 * - updateSettings: Function to update settings only
 * - updateBranding: Function to update branding only
 *
 * Usage:
 * @example
 * const { system, isLoading, updateSystem } = useSystem();
 *
 * if (isLoading) return <Loading />;
 * if (!system) return <Error />;
 *
 * console.log(system.name);
 * console.log(system.settings?.branding?.primary_color);
 *
 * await updateSystem({ name: 'New Name' });
 */
export const useSystem = () => {
  const { data, isLoading, error, refetch } = useGetSystemOrganization();
  const updateOrganization = useUpdateSystemOrganization();
  const updateSettingsMutation = useUpdateSystemSettings();
  const updateBrandingMutation = useUpdateBranding();

  // Auto-fetch on mount (similar to old Zustand store behavior)
  useEffect(() => {
    if (!data && !isLoading && !error) {
      void refetch();
    }
  }, [data, isLoading, error, refetch]);

  return {
    // Data
    system: data ?? null,
    isLoading,
    error,

    // Mutation functions (async)
    updateSystem: async (updates: OrganizationUpdate) => {
      return updateOrganization.mutateAsync(updates);
    },
    updateSettings: async (settings: SystemSettings) => {
      return updateSettingsMutation.mutateAsync(settings);
    },
    updateBranding: async (branding: BrandingSettings) => {
      return updateBrandingMutation.mutateAsync(branding);
    },

    // Mutation states
    isUpdating: updateOrganization.isPending || updateSettingsMutation.isPending || updateBrandingMutation.isPending,
    updateError: updateOrganization.error || updateSettingsMutation.error || updateBrandingMutation.error,

    // Manual refetch
    refetch,
  };
};
